import { formatUsPhoneToHtml } from '@eigtech/contacts-util'
import { NoteDataSourceEnum, type Note as ClaimNote } from '@eigtech/notes-types'
import { isJobCorn } from '@eigtech/restoration-types'
import { Corn } from '@eigtech/shared-corn'
import { useUserContext } from '@eigtech/ui-shared-auth'
import {
  augmentContact,
  getContactRoleLabel,
  useGetContact,
  useGetUserContact,
} from '@eigtech/ui-shared-contacts'
import { useDatesContext } from '@eigtech/ui-shared-dates'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  DataSource,
  HStack,
  Property,
  PropertyList,
  runIfFn,
  Stack,
  Text,
} from '@eigtech/ui-shared-dave'
import { InlineMention, MentionsDisplay } from '@eigtech/ui-shared-mentions'
import { getNoteDataSourceLabel, useNormalizedNoteText } from '@eigtech/ui-shared-notes'
import { formatPhoneNumber } from '@eigtech/ui-shared-phone'
import { Badge } from '@eigtech/ui/components/badge'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@eigtech/ui/components/hover-card'
import { cn } from '@eigtech/ui/lib/utils'
import { truncate } from 'lodash-es'
import { Mail, Phone } from 'lucide-react'
import { ReactNode } from 'react'
import { NoteImportStatus } from './NoteImportStatus'
import { NoteVisibility } from './NoteVisibility'

export type NoteCardProps = {
  actionsSlot?: ReactNode | ((note: ClaimNote) => ReactNode)
  entityCorn: Corn
  note: ClaimNote
  includePublishField?: boolean
}

export function NoteCard({
  actionsSlot,
  entityCorn,
  note,
  includePublishField = true,
}: NoteCardProps) {
  const normalizedMessageData = useNormalizedNoteText(note)

  const { PreferredDateTimeComponent } = useDatesContext()
  const { PreferredUserComponent } = useUserContext()

  const dataSource = note.dataSource
  const dataSourceLabel = dataSource
    ? getNoteDataSourceLabel(dataSource as NoteDataSourceEnum)
    : 'System'

  return (
    <Accordion allowToggle defaultIndex={undefined} w="full">
      <AccordionItem border="0">
        {({ isExpanded }) => (
          <Card>
            <CardHeader borderBottomWidth={isExpanded ? '1px' : '0px'} px="0" py="0" w="full">
              <HStack pr="4">
                <AccordionButton
                  flexGrow={1}
                  px="4"
                  py="3"
                  roundedBottom={isExpanded ? 'none' : 'lg'}
                  roundedTop="lg"
                  w="full"
                >
                  <HStack spacing="4" w="full">
                    <Stack flex="1" fontSize="sm" spacing="0" textAlign="left" w="full">
                      <PreferredDateTimeComponent
                        format="MMM dd yyyy, hh:mm aa"
                        property="createdOn"
                      >
                        {note.createdOn}
                      </PreferredDateTimeComponent>

                      <Text fontSize="md" fontWeight="bold">
                        {note.title
                          ? `${note.title}`
                          : truncate(normalizedMessageData, { length: 50 })}
                      </Text>
                    </Stack>
                    <AccordionIcon />
                  </HStack>
                </AccordionButton>

                <DataSource>{dataSourceLabel}</DataSource>
                {includePublishField && <NoteImportStatus entityCorn={entityCorn} note={note} />}
                {isJobCorn(entityCorn) && <NoteVisibility entityCorn={entityCorn} note={note} />}
                {runIfFn(actionsSlot, note)}
              </HStack>
            </CardHeader>

            <AccordionPanel p="0">
              {isExpanded && (
                <>
                  <CardBody>
                    <HStack>
                      <MentionsDisplay
                        MentionComponent={CustomMentionComponent}
                        className="cursor-text whitespace-pre-wrap"
                        text={normalizedMessageData}
                        triggers={['@']}
                      />
                    </HStack>
                  </CardBody>

                  <CardFooter>
                    <PropertyList>
                      <Property
                        fontSize="sm"
                        label="Created By"
                        value={
                          <PreferredUserComponent property="createdBy" user={note.createdBy} />
                        }
                      />
                      {note.userMetadata?.licenseNumber && (
                        <Property
                          fontSize="sm"
                          label="License #"
                          value={note.userMetadata.licenseNumber}
                        />
                      )}

                      <Property
                        fontSize="sm"
                        label="Created On"
                        value={
                          <PreferredDateTimeComponent
                            format="MM/dd/yyyy hh:mm aa"
                            property="createdOn"
                          >
                            {note.createdOn}
                          </PreferredDateTimeComponent>
                        }
                      />

                      <Property fontSize="sm" label="Category" value={note.category ?? '(None)'} />
                    </PropertyList>
                  </CardFooter>
                </>
              )}
            </AccordionPanel>
          </Card>
        )}
      </AccordionItem>
    </Accordion>
  )
}

/** Matches mention text like @Kasey Culmback(corn:contacts:contact:m-yzsWDsDwhgiB_mIsXG4) */
const MENTION_CORN_REGEX = /\((corn:[^)]+)\)/

function CustomMentionComponent({ children }: { children: string }) {
  const { data: userContact } = useGetUserContact()

  const mentionCornMatch = children.match(MENTION_CORN_REGEX)
  const mentionCornId = mentionCornMatch?.[1] ?? null
  const isUser =
    !!userContact?.contactId && !!mentionCornId && mentionCornId === userContact.contactId

  const { data: contact } = useGetContact(mentionCornId ?? '')

  const mentionDisplay = (
    <InlineMention className={cn(isUser && 'font-bold', 'cursor-help')}>
      {children.replace(MENTION_CORN_REGEX, '')}
    </InlineMention>
  )

  if (contact) {
    const augmentedContact = augmentContact(contact)
    return (
      <HoverCard>
        <HoverCardTrigger delay={300}>{mentionDisplay}</HoverCardTrigger>

        <HoverCardContent className="space-y-1.5">
          <div className={isUser ? 'font-bold' : 'font-semibold'}>
            {augmentedContact.contactName}
          </div>

          {!!augmentedContact.roles.length && (
            <div className="flex flex-wrap gap-1">
              {augmentedContact.roles.map(getContactRoleLabel).map((role) => (
                <Badge key={role} size="sm">
                  {role}
                </Badge>
              ))}
            </div>
          )}

          {!!augmentedContact.primaryEmail && (
            <div className="flex flex-nowrap items-center gap-2">
              <Mail className="size-5" />
              <a className="text-sm" href={`mailto:${augmentedContact.primaryEmail}`}>
                {augmentedContact.primaryEmail}
              </a>
            </div>
          )}

          {!!augmentedContact.primaryPhoneNumber && (
            <div className="flex flex-nowrap items-center gap-2">
              <Phone className="size-4" />
              <a
                className="text-sm"
                href={`tel:${formatUsPhoneToHtml(augmentedContact.primaryPhoneNumber)}`}
              >
                {formatPhoneNumber(augmentedContact.primaryPhoneNumber)}
              </a>
            </div>
          )}
        </HoverCardContent>
      </HoverCard>
    )
  }
  return mentionDisplay
}
